import { ICoordenadas } from 'modelos/src';

/**
 * Utilidades puras de conversión entre el modelo interno de la app
 * (`ICoordenadas { lat, lng }`) y el formato que usa Mapbox / GeoJSON
 * (`[lng, lat]`). El modelo `ICoordenadas` NO se toca: se consume tal cual.
 */

/** `ICoordenadas` → `[lng, lat]` (orden GeoJSON / Mapbox). */
export function latLngToLngLat(c: ICoordenadas): [number, number] {
  return [c.lng, c.lat];
}

/** `[lng, lat]` → `ICoordenadas`. */
export function lngLatToLatLng([lng, lat]: [number, number]): ICoordenadas {
  return { lat, lng };
}

/**
 * Convierte un anillo de `ICoordenadas` a coordenadas GeoJSON `[lng, lat][]`,
 * cerrando el anillo (el primer punto se repite al final) como exige GeoJSON.
 */
export function anilloToGeoJSON(coords: ICoordenadas[]): [number, number][] {
  const ring = (coords || []).map(latLngToLngLat);
  if (ring.length > 0) {
    const [pLng, pLat] = ring[0];
    const [uLng, uLat] = ring[ring.length - 1];
    if (pLng !== uLng || pLat !== uLat) {
      ring.push([pLng, pLat]);
    }
  }
  return ring;
}

/**
 * Convierte uno o más anillos (`ICoordenadas[][]`) en un Feature GeoJSON
 * de tipo Polygon. El primer anillo es el exterior; los siguientes, huecos.
 */
export function coordenadasToGeoJSONPolygon(
  rings: ICoordenadas[][],
  properties: Record<string, unknown> = {},
): GeoJSON.Feature<GeoJSON.Polygon> {
  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: (rings || []).map(anilloToGeoJSON),
    },
    properties,
  };
}

/**
 * Extrae los anillos de un Feature Polygon / MultiPolygon (o su geometría)
 * como `ICoordenadas[][]`. Para MultiPolygon devuelve solo los anillos
 * exteriores de cada polígono (sin huecos), coherente con `OsmService`.
 */
export function geoJSONToCoordenadas(
  geometry?: GeoJSON.Geometry | null,
): ICoordenadas[][] {
  if (!geometry) {
    return [];
  }
  const anillo = (puntos: GeoJSON.Position[]): ICoordenadas[] =>
    (puntos || []).map((p) => lngLatToLatLng([p[0], p[1]]));

  if (geometry.type === 'Polygon') {
    return geometry.coordinates.map(anillo);
  }
  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates.map((poly) => anillo(poly?.[0]));
  }
  return [];
}

/** Centro promedio de un anillo de coordenadas (o `undefined` si está vacío). */
export function getCentro(coords?: ICoordenadas[]): ICoordenadas | undefined {
  if (!coords?.length) {
    return undefined;
  }
  const sum = coords.reduce(
    (acc, c) => ({ lat: acc.lat + c.lat, lng: acc.lng + c.lng }),
    { lat: 0, lng: 0 },
  );
  return { lat: sum.lat / coords.length, lng: sum.lng / coords.length };
}
